import React from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Phone } from "lucide-react";
import { SITES } from "@/config/sites";
import { T } from "@/config/i18n";
import { useLanguage } from "@/contexts/LanguageContext";
import { CITY_META } from "@/config/cityPages";
import { VEHICLE_META } from "@/config/vehiclePages";
import { CALCULATOR_META } from "@/config/calculatorPages";
import { RMC_LOGO_IMAGE } from "@/config/imageSets";
import OptimizedImage from "@/components/site/OptimizedImage";

const FOOTER_BANKS = [
  { slug: "desjardins", fr: "Desjardins", en: "Desjardins" },
  { slug: "rbc", fr: "RBC", en: "RBC" },
  { slug: "td", fr: "TD", en: "TD" },
  { slug: "bmo", fr: "BMO", en: "BMO" },
  { slug: "scotia", fr: "Scotia", en: "Scotia" },
  { slug: "bnc", fr: "Banque Nationale", en: "National Bank" },
];

const FOOTER_LENDERS = [
  { slug: "desjardins", label: "Desjardins Auto" },
  { slug: "ia", label: "iA Auto Finance" },
  { slug: "eden-park", label: "Eden Park" },
  { slug: "iceberg", label: "Iceberg Finance" },
  { slug: "lac", label: "LAC" },
  { slug: "santander", label: "Santander" },
];

const FOOTER_COMPARISONS = [
  { slug: "location-vs-financement", fr: "Location vs Financement", en: "Lease vs Finance" },
  { slug: "banque-vs-preteur-specialise", fr: "Banque vs Prêteur", en: "Bank vs Lender" },
  { slug: "proposition-vs-faillite", fr: "Proposition vs Faillite", en: "Proposal vs Bankruptcy" },
  { slug: "concessionnaire-vs-comparateur", fr: "Concessionnaire vs Comparateur", en: "Dealership vs Marketplace" },
];

// Same tel: URI builder logic as SiteHeader — Quebec numbers need +1 prefix
// for desktop dialers to actually initiate a call.
function telHref(phone) {
  if (!phone) return "#";
  const digits = phone.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return `tel:${digits}`;
  const bare = digits.replace(/\D/g, "");
  if (bare.length === 10) return `tel:+1${bare}`;
  if (bare.length === 11 && bare.startsWith("1")) return `tel:+${bare}`;
  return `tel:${bare}`;
}

export default function SiteFooter() {
  const { siteKey } = useParams();
  const site = SITES[siteKey];
  const { lang } = useLanguage();
  const t = T[lang];
  if (!site) return null;
  const base = `/${siteKey}`;

  // Footer is intentionally white (matches header) so the brand logos —
  // which are designed for a white background — display cleanly.
  const bg = "#FFFFFF";
  const text = "#0F172A";
  const muted = "#475569";
  const border = "rgba(15,23,42,0.08)";

  return (
    <footer
      className="mt-24 border-t"
      style={{ borderColor: border, backgroundColor: bg }}
      data-testid="site-footer"
    >
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-4">
          <div>
            {site.logo ? (
              <OptimizedImage
                {...RMC_LOGO_IMAGE}
                alt={site.name}
                className="h-14 w-auto object-contain sm:h-16"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="flex items-center gap-2">
                <div
                  className="flex h-9 w-9 items-center justify-center text-base font-black text-white"
                  style={{ backgroundColor: site.theme.primary, borderRadius: site.theme.radius }}
                >
                  R
                </div>
                <span className="text-base font-black tracking-tight" style={{ color: text }}>
                  Rétablirmoncredit<span style={{ color: site.theme.primary }}>.com</span>
                </span>
              </div>
            )}
            <p className="mt-4 text-sm font-semibold" style={{ color: text }}>{site.domain}</p>
            {site.tagline && (
              <p className="mt-1 text-xs italic" style={{ color: site.theme.primary }}>
                {site.tagline}
              </p>
            )}
            <p className="mt-3 text-xs font-semibold uppercase tracking-wider" style={{ color: muted }}>
              {lang === "fr" ? "Une marque de" : "A brand of"}{" "}
              <span style={{ color: text }}>Crédit Auto Reset</span>
            </p>
            <p className="mt-3 text-xs leading-relaxed" style={{ color: muted }}>
              {t.footerLegal}
            </p>
          </div>

          <div>
            <h4 className="mb-3 text-sm font-semibold" style={{ color: text }}>
              {lang === "fr" ? "Navigation" : "Navigation"}
            </h4>
            <ul className="space-y-2 text-sm" style={{ color: muted }}>
              {Object.keys(site.pages).map((k) => {
                const p = site.pages[k];
                const path = p.path ? `${base}/${p.path}` : base;
                return (
                  <li key={k}>
                    <Link to={path} className="transition hover:opacity-70" data-testid={`footer-link-${k}`}>
                      {lang === "fr" ? p.labelFr : p.labelEn}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h4 className="mb-3 text-sm font-semibold" style={{ color: text }}>
              {lang === "fr" ? "Légal" : "Legal"}
            </h4>
            <ul className="space-y-2 text-sm" style={{ color: muted }}>
              <li>
                <Link to={`${base}/privacy`} className="transition hover:opacity-70" data-testid="footer-privacy">{t.privacy}</Link>
              </li>
              <li>
                <Link to={`${base}/terms`} className="transition hover:opacity-70" data-testid="footer-terms">{t.terms}</Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="mb-3 text-sm font-semibold" style={{ color: text }}>
              {lang === "fr" ? "Contact" : "Contact"}
            </h4>
            <ul className="space-y-2 text-sm" style={{ color: muted }}>
              <li className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0" style={{ color: site.theme.primary }} />
                <span>{site.address}</span>
              </li>
              <li>
                <a
                  href={telHref(site.phone)}
                  className="flex items-center gap-2 transition hover:opacity-70"
                  data-testid="footer-phone"
                >
                  <Phone className="h-4 w-4 shrink-0" style={{ color: site.theme.primary }} />
                  <span className="font-semibold" style={{ color: text }}>{site.phone}</span>
                </a>
              </li>
              <li className="text-xs" style={{ color: muted }}>
                {lang === "fr" ? "Réponse rapide pendant le traitement des demandes" : "Prompt reply while applications are being reviewed"}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 border-t pt-8" style={{ borderColor: border }}>
          <h4 className="text-xs font-bold uppercase tracking-widest" style={{ color: muted }}>
            {lang === "fr" ? "Explorer nos ressources" : "Explore our resources"}
          </h4>
          <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: site.theme.primary }}>
                {lang === "fr" ? "Refus bancaire" : "Bank refusal"}
              </div>
              <ul className="mt-2 space-y-1.5 text-sm" style={{ color: muted }}>
                {FOOTER_BANKS.map((b) => (
                  <li key={b.slug}>
                    <Link to={`${base}/refuse-par/${b.slug}`} className="transition hover:opacity-70">
                      {lang === "fr" ? `Refusé ${b.fr}` : `${b.en} refusal`}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: site.theme.primary }}>
                {lang === "fr" ? "Prêteurs" : "Lenders"}
              </div>
              <ul className="mt-2 space-y-1.5 text-sm" style={{ color: muted }}>
                {FOOTER_LENDERS.map((l) => (
                  <li key={l.slug}>
                    <Link to={`${base}/preteur/${l.slug}`} className="transition hover:opacity-70">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: site.theme.primary }}>
                {lang === "fr" ? "Comparaisons" : "Comparisons"}
              </div>
              <ul className="mt-2 space-y-1.5 text-sm" style={{ color: muted }}>
                {FOOTER_COMPARISONS.map((c) => (
                  <li key={c.slug}>
                    <Link to={`${base}/comparaison/${c.slug}`} className="transition hover:opacity-70">
                      {lang === "fr" ? c.fr : c.en}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: site.theme.primary }}>
                {lang === "fr" ? "Par ville" : "By city"}
              </div>
              <ul className="mt-2 space-y-1.5 text-sm" style={{ color: muted }}>
                {CITY_META.slice(0, 8).map((c) => (
                  <li key={c.slug}>
                    <Link to={`${base}/ville/${c.slug}`} className="transition hover:opacity-70">
                      {lang === "fr" ? c.fr : c.en}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-x-4 gap-y-1.5 text-xs" style={{ color: muted }}>
            <span className="font-bold uppercase tracking-widest" style={{ color: site.theme.primary }}>
              {lang === "fr" ? "Véhicules :" : "Vehicles:"}
            </span>
            {VEHICLE_META.map((v) => (
              <Link key={v.slug} to={`${base}/vehicule/${v.slug}`} className="transition hover:opacity-70">
                {lang === "fr" ? v.fr : v.en}
              </Link>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-xs" style={{ color: muted }}>
            <span className="font-bold uppercase tracking-widest" style={{ color: site.theme.primary }}>
              {lang === "fr" ? "Outils :" : "Tools:"}
            </span>
            <Link to={`${base}/${lang === "fr" ? "outils" : "tools"}`} className="font-semibold transition hover:opacity-70">
              {lang === "fr" ? "Tous les calculateurs" : "All calculators"}
            </Link>
            {CALCULATOR_META.map((c) => (
              <Link
                key={c.slug}
                to={`${base}/${lang === "fr" ? "outils" : "tools"}/${lang === "fr" ? c.slug : c.enSlug}`}
                className="transition hover:opacity-70"
              >
                {lang === "fr" ? c.frShort : c.enShort}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-10 border-t pt-6 text-xs" style={{ color: muted, borderColor: border }}>
          <p>{t.approvedNote}</p>
          <p className="mt-2">
            © {new Date().getFullYear()} {site.name} — {lang === "fr" ? "Une marque de" : "A brand of"} Crédit Auto Reset. {t.footerRights}
          </p>
          <p className="mt-2 opacity-75">
            {lang === "fr"
              ? "Tous les noms de prêteurs et logos affichés (Desjardins, TD, Scotiabank, Santander, iA Groupe financier, etc.) sont des marques de commerce de leurs propriétaires respectifs. Ils sont utilisés à des fins d'identification et de comparaison uniquement — leur présence n'implique aucune affiliation, endossement ou parrainage."
              : "All lender names and logos shown (Desjardins, TD, Scotiabank, Santander, iA Financial Group, etc.) are trademarks of their respective owners. They are used for identification and comparison purposes only — their presence does not imply any affiliation, endorsement or sponsorship."}
          </p>
        </div>
      </div>
    </footer>
  );
}
